// Data handler for authentication and file operations
// This makes API calls to the backend to handle file system operations

// Simulated responses storage for browser environment
const simulatedFS = {
  responses: {}
};

// Function to dynamically check if company folder exists in /campaigns
const checkCompanyExists = async (companyCode) => {
  try {
    console.log(`[Frontend] Checking company code: ${companyCode}`);
    
    // Make API call to check if company folder exists
    const url = `/api/campaigns/check/${encodeURIComponent(companyCode)}`;
    console.log(`[Frontend] Making request to: ${url}`);
    
    const response = await fetch(url, {
      method: 'GET',
    });
    
    console.log(`[Frontend] Response status: ${response.status}`);
    
    if (response.ok) {
      const result = await response.json();
      console.log(`[Frontend] Response data:`, result);
      return result.exists;
    }
    
    console.log(`[Frontend] Response not ok: ${response.status}`);
    return false;
  } catch (error) {
    console.error('[Frontend] Error checking company existence:', error);
    return false;
  }
};

// Function to validate employee credentials by reading Excel file
const validateEmployee = async (companyCode, employeeName, questionnaireCode) => {
  try {
    // Make API call to validate employee against Excel file in company folder
    const response = await fetch(`/api/campaigns/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        companyCode, 
        employeeName, 
        questionnaireCode 
      }),
    });
    
    if (response.ok) {
      const result = await response.json();
      return result.valid;
    }
    
    return false;
  } catch (error) {
    console.error('Error validating employee:', error);
    return false;
  }
};

export const authenticateUser = async (companyCode, employeeName, questionnaireCode) => {
  try {
    // Simulate processing delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Step 1: Check if company folder exists in /campaigns
    const companyExists = await checkCompanyExists(companyCode);
    if (!companyExists) {
      return { success: false, error: 'Error: Invalid Company Code' };
    }

    // Step 2: Validate employee credentials against Excel file
    const isValidEmployee = await validateEmployee(companyCode, employeeName, questionnaireCode);
    if (!isValidEmployee) {
      return { success: false, error: 'Invalid Employee Name & Employee Questionnaire Code' };
    }

    return { success: true, message: 'Authentication successful' };
  } catch (error) {
    console.error('Authentication error:', error);
    return { success: false, error: 'Authentication failed. Please try again.' };
  }
};

export const loadQuestions = async () => {
  try {
    // In a real implementation, this would fetch from the server
    // For now, we'll use the questions data directly
    const response = await fetch('/output/AIMaturity_Questions.json');
    if (!response.ok) {
      throw new Error('Failed to load questions');
    }
    const data = await response.json();
    return data.aiMaturityQuestions || [];
  } catch (error) {
    console.error('Error loading questions:', error);
    // Fallback to hardcoded questions for demo
    return [
      {
        order: 1,
        category: "Leadership & Vision",
        question: "1. What is the level of executive support for AI initiatives?",
        responseOptions: [
          { option1: "(1) No formal strategy; AI is ad-hoc." },
          { option2: "(2) Defined goals with limited buy-in." },
          { option3: "(3) AI is part of a formal strategy with strong support." },
          { option4: "(4) AI is a core part of the business strategy." }
        ],
        yourRating: ""
      },
      {
        order: 2,
        category: "Leadership & Vision",
        question: "2. How is AI success measured in the organization?",
        responseOptions: [
          { option1: "(1) Not measured." },
          { option2: "(2) Measured anecdotally." },
          { option3: "(3) Measured by project ROI." },
          { option4: "(4) Measured by strategic impact on business growth." }
        ],
        yourRating: ""
      }
    ];
  }
};

export const checkExistingResponse = async (companyCode, employeeName, questionnaireCode) => {
  try {
    const response = await fetch('/api/responses/check', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ companyCode, employeeName, questionnaireCode }),
    });
    
    if (response.ok) {
      const result = await response.json();
      return result.exists ? result : null;
    }
    
    return null;
  } catch (error) {
    console.error('Error checking existing response:', error);
    return null;
  }
};

export const saveResponse = async (userInfo, questions, responses, isPartialSave = false) => {
  try {
    // Prepare response data - convert from our internal key format back to the expected format
    const responseData = questions.map(question => {
      const questionKey = `${question.category}|||${question.question}`;
      return {
        order: question.order,
        category: question.category,
        question: question.question,
        employeeResponse: responses[questionKey] || null
      };
    });

    const response = await fetch('/api/responses/save', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        userInfo, 
        responses: responseData,
        isPartialSave
      }),
    });
    
    if (response.ok) {
      const result = await response.json();
      return result;
    } else {
      const error = await response.json();
      throw new Error(error.error || 'Failed to save response');
    }
  } catch (error) {
    console.error('Error saving response:', error);
    throw error;
  }
};